import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Star } from 'lucide-react';
import { LogoLockup } from '../components/Logo';
import { FEATURED, optimizeImg } from '../content/mediaLibrary';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const { t } = useLanguage();
  useSEO({ title: t.seo.home.title, description: t.seo.home.description });

  // Scroll-driven parallax for the storefront section
  const storefrontRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: storefrontRef,
    offset: ['start end', 'end start'],
  });
  // Image: subtle ken-burns zoom (scale only — no Y translate that would expose edges)
  const imgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1.02, 1.08]);
  // Card: rises from below + slight scale on scroll-in, settles mid-view
  const cardY = useTransform(scrollYProgress, [0, 0.4, 0.7], [80, 0, -20]);
  const cardOpacity = useTransform(scrollYProgress, [0, 0.25, 0.75, 1], [0, 1, 1, 0.85]);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* HERO - Full-bleed cinematic video background with floating glass card */}
      <section
        className="relative w-full h-[100svh] min-h-[600px] overflow-hidden bg-[#1A1A18]"
        data-testid="hero-section"
      >
        {/* Background video — fills entire hero. Poster shows instantly while
            the video downloads, eliminating the visual gap. */}
        <video
          src={FEATURED.heroVideo}
          poster={optimizeImg(FEATURED.heroPoster, 1600, 78)}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        />

        {/* Multi-layer overlay for legibility + mood */}
        {/* Left-to-center dark wash so text reads cleanly */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(90deg, rgba(26,26,24,0.80) 0%, rgba(26,26,24,0.55) 35%, rgba(26,26,24,0.20) 65%, rgba(26,26,24,0.45) 100%)',
          }}
        />
        {/* Subtle top/bottom vignette */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(26,26,24,0.55) 0%, transparent 25%, transparent 70%, rgba(26,26,24,0.70) 100%)',
          }}
        />

        {/* Glass card — floats centered-left */}
        <div className="relative z-10 h-full w-full flex items-center">
          <div className="w-full max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              className="relative max-w-xl lg:max-w-2xl"
              data-testid="hero-card"
            >
              {/* Glass morphism panel */}
              <div
                className="relative p-7 sm:p-10 lg:p-14 border border-white/15 backdrop-blur-md shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(26,26,24,0.45) 0%, rgba(37,52,44,0.35) 100%)',
                }}
              >
                {/* Brick-orange accent bar top-left */}
                <span
                  className="absolute top-0 left-0 h-[2px] w-16 bg-[#A14223]"
                  aria-hidden
                />

                {/* Eyebrow */}
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="est-badge inline-block mb-6 lg:mb-8"
                  data-testid="hero-badge"
                >
                  {t.hero.badge}
                </motion.span>

                {/* Headline */}
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.8 }}
                  className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-normal tracking-tight text-[#F5F0E8] leading-[0.98] mb-6 lg:mb-8"
                  data-testid="hero-headline"
                >
                  {t.hero.headline.split('.')[0]}.
                </motion.h1>

                {/* Subheadline */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.0 }}
                  className="font-body font-light text-sm sm:text-base lg:text-lg text-[#F5F0E8]/80 max-w-md mb-8 lg:mb-10"
                  style={{ lineHeight: 1.85 }}
                  data-testid="hero-subheadline"
                >
                  {t.hero.subheadline}
                </motion.p>

                {/* CTAs */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                  className="flex flex-col sm:flex-row gap-3 sm:gap-4"
                >
                  <Link
                    to="/booking"
                    className="btn-cta inline-flex items-center justify-center gap-3"
                    data-testid="hero-cta-primary"
                  >
                    {t.hero.cta}
                    <ArrowRight size={14} />
                  </Link>
                  <Link
                    to="/services"
                    className="btn-outline btn-outline-light text-center"
                    data-testid="hero-cta-secondary"
                  >
                    {t.hero.ctaSecondary}
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll hint at the bottom */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#F5F0E8]/60 pointer-events-none"
          data-testid="hero-scroll-hint"
        >
          <span className="eyebrow text-[10px] tracking-[0.3em]">SCROLL</span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
            className="w-[1px] h-10 bg-[#F5F0E8]/40"
          />
        </motion.div>
      </section>

      {/* COMMITMENTS - Scroll-pinned cycle (one at a time, cross-fading) */}
      <CommitmentsCycle items={t.pillars.items} title={t.pillars.title} intro={t.pillars.intro} />

      {/* REVIEWS - Live Google reviews, same scroll-pinned cycle UX */}
      <ReviewsCycle t={t.reviews} />

      {/* STOREFRONT - Full-bleed edge-to-edge image with scroll-driven effects */}
      <section
        ref={storefrontRef}
        className="relative w-full overflow-hidden bg-[#1A1A18]"
        data-testid="storefront-section"
      >
        {/* Image at natural aspect — covers full width, no cropping, no letterbox */}
        <motion.img
          style={{ scale: imgScale }}
          src="https://images.weserv.nl/?url=customer-assets.emergentagent.com%2Fjob_italian-grooming%2Fartifacts%2F71jl4dyh_ChatGPT%20Image%20Apr%2022%2C%202026%2C%2007_52_48%20PM.png&w=2400&output=webp&q=92&we"
          alt="Casanova Salon Barbier — 56 rue Jean-Talon Ouest, Petite-Italie"
          loading="lazy"
          decoding="async"
          className="block w-full h-auto"
          style={{
            filter: 'contrast(1.06) saturate(0.95) brightness(0.97)',
            transformOrigin: 'center center',
          }}
          data-testid="storefront-image"
        />

        {/* Warm cinematic grade + vignette */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none mix-blend-multiply"
          style={{
            background:
              'linear-gradient(180deg, rgba(255,210,160,0.04) 0%, rgba(26,26,24,0.08) 50%, rgba(26,26,24,0.22) 100%)',
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.30) 100%)',
          }}
        />

        {/* Caption card — centered, positioned just above the awning (~ 55% down) */}
        <motion.div
          style={{ y: cardY, opacity: cardOpacity }}
          className="absolute left-0 right-0 top-[38%] sm:top-[40%] lg:top-[42%] flex justify-center px-4"
          data-testid="storefront-caption"
        >
          <div
            className="relative px-6 py-5 lg:px-10 lg:py-7 backdrop-blur-[4px] border border-white/25 text-center w-full max-w-sm lg:max-w-md"
            style={{
              background:
                'linear-gradient(135deg, rgba(37,52,44,0.65) 0%, rgba(37,52,44,0.45) 100%)',
              boxShadow: '0 25px 60px -15px rgba(0,0,0,0.6)',
            }}
          >
            <span
              className="absolute left-1/2 -translate-x-1/2 top-0 h-[2px] w-14 bg-[#A14223]"
              aria-hidden
            />
            <span className="eyebrow text-[#F5F0E8]/80 block mb-2 mt-1">
              {t.storefront.eyebrow}
            </span>
            <h3 className="font-display text-xl sm:text-2xl lg:text-3xl text-[#F5F0E8] leading-tight mb-1">
              {t.storefront.addressLine1}
            </h3>
            <p className="font-body font-light text-[11px] lg:text-xs text-[#F5F0E8]/75 tracking-[0.2em] uppercase mb-3 lg:mb-4">
              {t.storefront.addressLine2}
            </p>
            <div className="w-8 h-[1px] bg-[#A14223]/80 mx-auto mb-3 lg:mb-4" />
            <p
              className="font-display italic text-sm sm:text-base lg:text-lg text-[#F5F0E8]/95 leading-relaxed max-w-xs mx-auto"
              style={{ lineHeight: 1.55 }}
            >
              {t.storefront.caption}
            </p>
          </div>
        </motion.div>
      </section>

      {/* STORY - Split Section on Noir */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-screen bg-[#1A1A18]" data-testid="story-section">
        {/* Left - Video */}
        <motion.div 
          initial={{ opacity: 0, scale: 1.1 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="relative min-h-[60vh] lg:min-h-screen order-2 lg:order-1 overflow-hidden"
        >
          <video
            src={FEATURED.storyVideo}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* Right - Text */}
        <div className="flex flex-col justify-center px-8 lg:px-16 py-16 lg:py-0 text-[#F5F0E8] order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="deco-line" />
              <span className="eyebrow text-[#8A8780]">{t.story.overline}</span>
            </div>
            
            <h2 className="font-display text-4xl lg:text-5xl xl:text-6xl font-normal tracking-tight mb-8 leading-[1.1]">
              {t.story.title}
            </h2>
            
            <div className="space-y-6 font-body font-light text-sm text-white/70 max-w-lg" style={{ lineHeight: 1.85 }}>
              <p>{t.story.p1}</p>
              <p>{t.story.p2}</p>
            </div>
            
            {/* Quote - Playfair Italic per brand book */}
            <blockquote className="mt-10 pl-6 border-l-2 border-[#A14223] font-display italic text-xl lg:text-2xl text-white/90 leading-relaxed">
              {t.story.quote}
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* CTA MARQUEE - Brick Orange */}
      <section className="py-6 bg-[#A14223] overflow-hidden" data-testid="cta-marquee">
        <div className="animate-marquee whitespace-nowrap flex">
          {[...Array(8)].map((_, i) => (
            <span key={i} className="mx-8 font-display text-3xl lg:text-4xl text-[#F5F0E8] font-normal tracking-tight">
              {t.hero.headline} <span className="mx-4 opacity-40">·</span>
            </span>
          ))}
        </div>
      </section>

      {/* FINAL CTA - On Parchment */}
      <section className="py-24 lg:py-32 bg-[#F5F0E8]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Brick Orange Logo Lockup */}
            <div className="flex justify-center mb-12">
              <LogoLockup variant="brick" size="lg" />
            </div>
            
            <h2 className="font-display text-4xl lg:text-6xl font-normal tracking-tight text-[#1A1A18] mb-8">
              {t.booking.title}
            </h2>
            <Link to="/booking" className="btn-cta inline-flex items-center gap-3" data-testid="final-cta-button">
              {t.hero.cta}
              <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;

// -----------------------------------------------------------------------------
// CommitmentsCycle — scroll-pinned stack. One commitment shows at a time,
// cross-fades to the next as the user scrolls.
// Uses a plain scroll listener for reliable behavior across desktop and mobile.
// -----------------------------------------------------------------------------
const CommitmentsCycle = ({ items, title, intro }) => {
  const wrapperRef = useRef(null);
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const onScroll = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // Pinned scroll range = sectionHeight - vh (the sticky child is vh tall)
      const pinRange = el.offsetHeight - vh;
      // How far into the pinned range the user has scrolled (0 → 1)
      // -rect.top equals page-scroll distance since section entered top of viewport
      const scrolled = -rect.top;
      let p = scrolled / pinRange;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      setProgress(p);
    };
    onScroll(); // initial
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // Each item owns 1/N of the progress. The fractional position within its
  // segment (0-1) is used to drive its own opacity/scale.
  const total = items.length;
  const step = 1 / total;

  return (
    <section
      ref={wrapperRef}
      className="relative texture-grain"
      style={{
        height: `${total * 100}vh`,
        background:
          'radial-gradient(ellipse at top left, #F5F0E8 0%, #EFE5D2 50%, #E8DDC6 100%)',
      }}
      data-testid="pillars-section"
    >
      {/* Sticky stage — split into [header row] + [flex-centered content] */}
      <div className="sticky top-0 h-screen w-full overflow-hidden flex flex-col pt-24 lg:pt-28">
        {/* Header strip — pinned to top of the sticky area, below the floating nav */}
        <div className="px-6 lg:px-12 max-w-7xl mx-auto w-full flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="deco-line" />
            <span className="eyebrow text-[#8A8780]">{title}</span>
          </div>
          <p className="mt-3 font-display italic text-base lg:text-lg text-[#1A1A18]/50 max-w-md">
            {intro}
          </p>
        </div>

        {/* Stacked commitments — fill remaining height, vertically centered */}
        <div className="relative flex-1 w-full max-w-4xl mx-auto px-6 lg:px-12 flex items-center justify-center">
          {items.map((item, i) => {
            const segStart = i * step;
            // Local progress within this segment: 0 at segStart, 1 at segStart+step
            const local = (progress - segStart) / step;
            // Opacity: fade-in 0 → 1 over first 25 %, stay at 1 for 50 %,
            // fade-out 1 → 0 over last 25 %
            let opacity = 0;
            if (local >= 0 && local <= 1) {
              if (local < 0.25) opacity = local / 0.25;
              else if (local < 0.75) opacity = 1;
              else opacity = 1 - (local - 0.75) / 0.25;
            }
            // Drift y ∈ [40, 0, 0, -40] along the same envelope
            let y = 0;
            if (local < 0) y = 40;
            else if (local < 0.25) y = 40 - (local / 0.25) * 40;
            else if (local < 0.75) y = 0;
            else if (local <= 1) y = -((local - 0.75) / 0.25) * 40;
            else y = -40;
            const scale = 0.96 + 0.04 * opacity;
            const number = String(i + 1).padStart(2, '0');

            return (
              <div
                key={i}
                className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 lg:px-0 will-change-transform pointer-events-none"
                style={{
                  opacity,
                  transform: `translateY(${y}px) scale(${scale})`,
                  transition: 'opacity 120ms linear, transform 120ms linear',
                }}
                data-testid={`commitment-${i + 1}`}
              >
                <span
                  className="font-display text-[6rem] sm:text-[8rem] lg:text-[11rem] leading-none text-[#A14223]/90 mb-4 lg:mb-6"
                  aria-hidden
                >
                  {number}
                </span>
                <div className="w-16 h-[2px] bg-[#A14223] mb-6 lg:mb-8" />
                <h3 className="font-display text-3xl sm:text-4xl lg:text-6xl text-[#1A1A18] tracking-tight leading-[1.05] mb-6 lg:mb-8 max-w-3xl">
                  {item.title}
                </h3>
                <p
                  className="font-body font-light text-base lg:text-lg text-[#1A1A18]/70 max-w-xl leading-relaxed"
                  style={{ lineHeight: 1.9 }}
                >
                  {item.body}
                </p>
                <span className="mt-10 lg:mt-14 eyebrow text-[#8A8780]">
                  {number} / {String(total).padStart(2, '0')}
                </span>
              </div>
            );
          })}
        </div>

        {/* Progress dots */}
        <div
          className="absolute right-5 lg:right-10 top-1/2 -translate-y-1/2 flex flex-col gap-3 z-20"
          data-testid="commitment-progress"
        >
          {items.map((_, i) => {
            const segStart = i * step;
            const local = (progress - segStart) / step;
            const active = local >= 0 && local <= 1;
            return (
              <span
                key={i}
                className="block w-1.5 h-1.5 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: active ? '#A14223' : 'rgba(26,26,24,0.25)',
                  transform: active ? 'scale(1.6)' : 'scale(1)',
                }}
                data-testid={`progress-dot-${i + 1}`}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

// -----------------------------------------------------------------------------
// ReviewsCycle — same scroll-pinned UX as CommitmentsCycle, but cards carry
// a star rating, quote, author + relative date. Fetches live from /api/reviews.
// -----------------------------------------------------------------------------
const ReviewsCycle = ({ t }) => {
  const [reviews, setReviews] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const wrapperRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews`);
        if (cancel) return;
        setReviews(data.reviews || []);
        setMeta({
          placeName: data.place_name,
          rating: data.overall_rating,
          total: data.total_reviews,
          source: data.source,
        });
      } catch (e) {
        console.error('Failed to load reviews', e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const pinRange = el.offsetHeight - vh;
      const scrolled = -rect.top;
      let p = scrolled / pinRange;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      setProgress(p);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [reviews.length]);

  if (loading || reviews.length === 0) {
    return (
      <section
        className="relative bg-[#25342C] py-24 text-center"
        data-testid="reviews-loading"
      >
        <p className="eyebrow text-[#F5F0E8]/50">{t.fallback}</p>
      </section>
    );
  }

  const total = reviews.length;
  const step = 1 / total;

  return (
    <section
      ref={wrapperRef}
      className="relative"
      style={{
        height: `${total * 100}vh`,
        background:
          'radial-gradient(ellipse at top right, #2E4138 0%, #25342C 50%, #1A241F 100%)',
      }}
      data-testid="reviews-section"
    >
      {/* Sticky stage — header row + flex-centered reviews */}
      <div className="sticky top-0 h-screen w-full overflow-hidden flex flex-col pt-24 lg:pt-28">
        {/* Header strip — below the floating nav */}
        <div className="px-6 lg:px-12 max-w-7xl mx-auto w-full flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="deco-line bg-[#F5F0E8]/30" />
            <span className="eyebrow text-[#F5F0E8]/70">{t.eyebrow}</span>
          </div>
          <div className="mt-3 flex items-center gap-4 flex-wrap">
            <h2 className="font-display italic text-base lg:text-lg text-[#F5F0E8]/60">
              {t.title}
            </h2>
            {meta?.rating && (
              <div className="flex items-center gap-2">
                <StarRow value={meta.rating} />
                <span className="font-body text-xs text-[#F5F0E8]/70 tracking-widest">
                  {meta.rating.toFixed(1)}
                  {meta.total ? ` · ${meta.total}` : ''}
                </span>
              </div>
            )}
          </div>
          <p className="mt-2 font-body font-light text-xs text-[#F5F0E8]/45 max-w-md">
            {t.intro}
          </p>
        </div>

        {/* Stacked review panels */}
        <div className="relative flex-1 w-full max-w-4xl mx-auto px-6 lg:px-12 flex items-center justify-center">
          {reviews.map((r, i) => {
            const segStart = i * step;
            const local = (progress - segStart) / step;
            let opacity = 0;
            if (local >= 0 && local <= 1) {
              if (local < 0.25) opacity = local / 0.25;
              else if (local < 0.75) opacity = 1;
              else opacity = 1 - (local - 0.75) / 0.25;
            }
            let y = 0;
            if (local < 0) y = 40;
            else if (local < 0.25) y = 40 - (local / 0.25) * 40;
            else if (local < 0.75) y = 0;
            else if (local <= 1) y = -((local - 0.75) / 0.25) * 40;
            else y = -40;
            const scale = 0.96 + 0.04 * opacity;

            return (
              <div
                key={i}
                className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 lg:px-0 will-change-transform pointer-events-none"
                style={{
                  opacity,
                  transform: `translateY(${y}px) scale(${scale})`,
                  transition: 'opacity 120ms linear, transform 120ms linear',
                }}
                data-testid={`review-${i + 1}`}
              >
                {/* Stars */}
                <StarRow value={r.rating} large />

                {/* Quote mark — decorative */}
                <span
                  className="font-display text-[6rem] sm:text-[8rem] lg:text-[10rem] leading-[0.6] text-[#A14223]/80 mt-6 mb-2"
                  aria-hidden
                >
                  “
                </span>

                {/* Review text */}
                <p className="font-display italic text-xl sm:text-2xl lg:text-4xl text-[#F5F0E8] leading-snug max-w-3xl mb-8 lg:mb-10">
                  {r.text}
                </p>

                <div className="w-10 h-[1px] bg-[#A14223]/70 mb-5" />

                {/* Author */}
                <span className="font-display text-lg lg:text-2xl text-[#F5F0E8]">
                  {r.author_name}
                </span>
                <span className="mt-1 eyebrow text-[#F5F0E8]/50">
                  {r.relative_time_description}
                </span>

                {/* Step counter */}
                <span className="mt-8 eyebrow text-[#F5F0E8]/40">
                  {String(i + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                </span>
              </div>
            );
          })}
        </div>

        {/* Progress dots */}
        <div
          className="absolute right-5 lg:right-10 top-1/2 -translate-y-1/2 flex flex-col gap-3 z-20"
          data-testid="reviews-progress"
        >
          {reviews.map((_, i) => {
            const segStart = i * step;
            const local = (progress - segStart) / step;
            const active = local >= 0 && local <= 1;
            return (
              <span
                key={i}
                className="block w-1.5 h-1.5 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: active ? '#A14223' : 'rgba(245,240,232,0.25)',
                  transform: active ? 'scale(1.6)' : 'scale(1)',
                }}
              />
            );
          })}
        </div>

        {/* "View all on Google" link — bottom-center */}
        <a
          href="https://www.google.com/maps/search/?api=1&query=Casanova+Salon+Barbier+Montreal"
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 eyebrow text-[10px] text-[#F5F0E8]/60 hover:text-[#F5F0E8] transition-colors duration-300 tracking-[0.3em]"
          data-testid="reviews-google-link"
        >
          {t.viewAllLabel} →
        </a>
      </div>
    </section>
  );
};

// Small star row helper
const StarRow = ({ value = 5, large = false }) => {
  const rounded = Math.round(value);
  const size = large ? 20 : 14;
  return (
    <div className="flex items-center gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          size={size}
          className={i < rounded ? 'fill-[#E0A838] text-[#E0A838]' : 'text-[#F5F0E8]/20'}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
};

